import {
  CountriesAlpha2,
  countriesAlpha2,
  isRegionState,
  statesForCountry,
  toStateLabel
} from '@sequencehq/api'
import { TaxRate } from './taxRates.types'
import { percentageFromDecimal } from '@sequencehq/utils'

/**
 * Builds the summary shown when a customer is missing a tax rate.
 *
 * The state is only named when the country is already covered by a nationwide
 * rate but the state-specific rate is the actual gap (PULSE-1316 validates US/CA
 * tax at the state level). When nothing is configured for the country yet, the
 * nationwide wording better reflects what's missing, so we avoid naming the
 * state just because the customer happens to have one.
 */
export const getMissingTaxRateMessage = ({
  country,
  state,
  taxRates
}: {
  country?: CountriesAlpha2
  state?: string
  taxRates: Pick<TaxRate, 'country' | 'region'>[]
}): string => {
  if (!country) {
    return `Customer has no country specified`
  }

  const taxRatesForCountry = taxRates.filter(rate => rate.country === country)
  const validState =
    isRegionState(state) && statesForCountry(country).includes(state)
      ? state
      : undefined
  const hasNationwideRate = taxRatesForCountry.some(rate => !rate.region)
  const hasStateSpecificRate =
    validState !== undefined &&
    taxRatesForCountry.some(rate => rate.region === validState)

  if (validState && hasNationwideRate && !hasStateSpecificRate) {
    return `Add tax rate for ${toStateLabel(validState)}, ${countriesAlpha2[country]}`
  }

  return `Add tax rates for ${countriesAlpha2[country]}`
}

const formatTaxRateLabel = (taxRate: TaxRate) =>
  `${taxRate.taxCategory.name}: ${percentageFromDecimal(taxRate.amount)}%`

export const getTaxRateDescription = (rates: TaxRate[]) => {
  const groupA = rates.slice(0, 2)
  const rest = rates.slice(2)

  if (rest.length === 0) {
    return `${groupA.map(formatTaxRateLabel).join(' and ')}.`
  }
  return `${groupA.map(formatTaxRateLabel).join(', ')}, and ${rest.length} more.`
}

export const getTaxRatesWithCategories = (taxRates: TaxRate[]) => {
  const taxRatesWithgroupingKey = taxRates
    .filter(rate => rate.taxCategory?.id)
    .map(taxRate => {
      const region = isRegionState(taxRate.region) ? taxRate.region : undefined
      const country = taxRate.country
      const groupingKey = [country, region].filter(Boolean).join('_')
      return { ...taxRate, groupingKey }
    })

  const taxRatesSortedByKey = taxRatesWithgroupingKey.sort((a, b) => {
    return a.groupingKey.localeCompare(b.groupingKey)
  })

  const groupedByKey = taxRatesSortedByKey.reduce(
    (acc, taxRate) => ({
      ...acc,
      [taxRate.groupingKey]: [
        ...(acc[taxRate.groupingKey] || []),
        taxRate
      ].sort(
        (a, b) =>
          a.taxCategory.name?.localeCompare(b.taxCategory.name || '') || -1
      )
    }),
    {} as Record<string, TaxRate[]>
  )

  const groupedByCountry = taxRatesSortedByKey.reduce(
    (acc, taxRate) => ({
      ...acc,
      [taxRate.country]: [...(acc[taxRate.country] || []), taxRate].sort(
        (a, b) =>
          a.taxCategory.name?.localeCompare(b.taxCategory.name || '') || -1
      )
    }),
    {} as Record<CountriesAlpha2, TaxRate[]>
  )
  return {
    groupedByKey,
    groupedByCountry
  }
}
